/**
 * Отложенная загрузка по видимости.
 *
 * Картинки ПИМа едут не прямой ссылкой в `<img>`, а авторизованным запросом
 * (см. authorizedMedia): токен лежит в localStorage, и браузер не приложит его
 * к `src`. Побочный эффект — встроенный `loading="lazy"` бесполезен: к моменту,
 * когда `<img>` появляется в разметке, байты уже скачаны. Запрос уходит на
 * монтировании, то есть порция каталога из 60 карточек занимает очередь целиком,
 * и верхний экран заполняется наравне с плитками, до которых не дошли.
 *
 * Хук возвращает ref для контейнера плитки и признак «рядом с экраном».
 * Признак взводится один раз и назад не сбрасывается: уехавшая вверх плитка не
 * должна отменять уже начатую загрузку и заново просить её при возврате.
 */

import { useEffect, useState } from 'react'

/**
 * Запас за краем экрана. 600px — примерно два ряда плиток каталога: к моменту,
 * когда ряд доезжает до глаз, картинка уже загружена, но и вся страница разом
 * в сеть не уходит.
 */
export const NEAR_VIEWPORT_ROOT_MARGIN = '600px'

export interface NearViewportState {
  /** Вешается на контейнер плитки — за ним и следит наблюдатель. */
  ref: (node: Element | null) => void
  /** Плитка в зоне видимости (или наблюдение невозможно) — можно грузить. */
  near: boolean
}

/**
 * Без IntersectionObserver (jsdom в тестах, старые сборки WebView) наблюдать
 * нечем — тогда грузим сразу, как было до отложенной загрузки.
 */
function observerAvailable(): boolean {
  return typeof IntersectionObserver !== 'undefined'
}

export function useNearViewport(rootMargin: string = NEAR_VIEWPORT_ROOT_MARGIN): NearViewportState {
  const [node, setNode] = useState<Element | null>(null)
  const [near, setNear] = useState<boolean>(() => !observerAvailable())

  useEffect(() => {
    if (near || !node || !observerAvailable()) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        setNear(true)
        observer.disconnect()
      },
      { rootMargin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [near, node, rootMargin])

  return { ref: setNode, near }
}
